'use client';

import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import {
  SCROLLBAR_CLASS,
  SCROLLBAR_TRACK_BG_CLASS,
  SCROLLBAR_TRACK_BORDER_CLASS,
} from '../../lib/scrollbar';
import { type SColor } from '../../lib/color';
import { SIcon, type SIconName } from '../SIcon';
import { SCheckbox } from '../SCheckbox';
import { SCircleProgress } from '../SCircleProgress';
import { SPagination } from '../SPagination';
import { SSelect, type SSelectOption } from '../SSelect';

export type SRow = Record<string, any>;

const DEFAULT_ROW_HEIGHT = 44;
const DENSE_ROW_HEIGHT = 32;
/** 선택 체크박스 컬럼 너비 (sd-table TABLE_SELECTABLE_COLUMN_WIDTH) */
const SELECTABLE_COLUMN_WIDTH = 48;
const DEFAULT_ROWS_PER_PAGE_OPTION: SSelectOption[] = [
  { label: '10개씩 보기', value: 10 },
  { label: '25개씩 보기', value: 25 },
  { label: '50개씩 보기', value: 50 },
  { label: '100개씩 보기', value: 100 },
];

/** `STableColumn.renderCell`에 전달되는 컨텍스트 */
export interface STableCellContext {
  row: SRow;
  /** `field` + `format`이 적용된 표시 값 */
  value: any;
  column: STableColumn;
  /** 보이는 컬럼 기준 인덱스 */
  colIndex: number;
  /** 현재 렌더 중인 행 목록 기준 인덱스 (페이지/가상 스크롤 윈도우 기준) */
  rowIndex: number;
  /** 행 선택 여부 */
  isSelected: boolean;
  /** STable이 이 셀에 적용하려던 클래스 (구분선·말줄임·sticky 그림자 포함) */
  className: string;
  /** STable이 이 셀에 적용하려던 스타일 (패딩·정렬·폭·sticky offset 포함) */
  style: CSSProperties;
}

export interface STableColumn {
  name: string;
  label: string;
  /** 값 접근: 필드명 또는 접근 함수 */
  field: string | ((row: SRow) => any);
  align?: 'left' | 'center' | 'right';
  width?: string;
  /** 고정폭 없이 자동 너비 (sticky/resize 계산에서 제외) */
  autoWidth?: boolean;
  /** 리사이즈 최소/최대 너비 (px) */
  minWidth?: number;
  maxWidth?: number;
  /** 표시 포맷 */
  format?: (value: any, row: SRow) => string;
  visible?: boolean;
  icon?: SIconName;
  /** 헤더 아이콘 색상. 팔레트 키(`grey_65`, `red_95` …) 또는 임의 CSS 색상 */
  iconColor?: SColor;
  /** 셀 커스텀 렌더 (React 확장) — `<td>` 안쪽 내용만 교체한다 */
  render?: (row: SRow, value: any) => ReactNode;
  /**
   * 셀(`<td>`) 자체를 커스텀 렌더 (React 확장). 반환한 엘리먼트가 그대로 `<td>`가 되므로
   * `colSpan` / `rowSpan`을 직접 지정할 수 있다. `render`보다 우선한다.
   *
   * - `ctx.className` / `ctx.style`을 그대로 펼쳐야 구분선·정렬·sticky 등 기본 스타일이 유지된다.
   * - `null`(또는 `false`)을 반환하면 그 자리에는 `<td>`를 만들지 않는다.
   *   다른 셀의 `colSpan` / `rowSpan`에 덮이는 자리를 이렇게 비운다.
   * - 병합은 sticky 컬럼(인덱스 기준 offset 계산)과 함께 쓰지 않는 것을 전제로 한다.
   *   `rowSpan`은 행을 잘라 쓰는 가상 스크롤 / 내부 페이지네이션과 경계에서 어긋날 수 있다.
   */
  renderCell?: (ctx: STableCellContext) => ReactNode;
  /** 헤더 셀 커스텀 렌더 (React 확장) */
  renderHeader?: (column: STableColumn, index: number) => ReactNode;
  /** 본문 셀 커스텀 클래스. 함수를 주면 행마다 다른 클래스를 반환할 수 있다 */
  tdClass?: string | ((row: SRow) => string | undefined);
  /** 헤더 셀 커스텀 클래스/스타일 (sd-table thClass/thStyle) */
  thClass?: string;
  thStyle?: CSSProperties;
}

export interface STablePagination {
  page: number;
  rowsPerPage: number;
  lastPage?: number;
}

export interface STableStickyColumn {
  left?: number;
  right?: number;
}

/** STable ref 명령형 API (sd-table @Method 대응) */
export interface STableHandle {
  /** 행 선택 여부 */
  isRowSelected: (row: SRow) => boolean;
  /** 주어진 행들의 전체 선택 상태 (true/false/null=indeterminate) */
  getIsAllChecked: (rows: SRow[]) => boolean | null;
  /** 내부 페이지네이션 정보 (useInternalPagination 아니면 null) */
  getPaginationInfo: () => {
    startIndex: number;
    endIndex: number;
    currentPage: number;
    rowsPerPage: number;
  } | null;
  /** 컬럼 sticky 스타일 (CSS 변수 + 고정폭) */
  getStickyStyle: (colIdx: number) => CSSProperties;
  /** 가상 스크롤 총 행 수 설정 */
  setRowCount: (count: number) => void;
  /** 단일 행 선택 토글 (sd-table updateRowSelect) — onSelectedChange 발생 */
  updateRowSelect: (row: SRow) => void;
  /** 주어진 행들 전체 선택/해제 (sd-table toggleSelectAll) — onSelectedChange 발생 */
  toggleSelectAll: (checked: boolean, rows: SRow[]) => void;
}

export interface STableProps {
  columns?: STableColumn[];
  rows?: SRow[];
  /** 행 식별 필드 */
  rowKey?: string;
  /** 행 선택 체크박스 */
  selectable?: boolean;
  selected?: SRow[];
  onSelectedChange?: (rows: SRow[]) => void;
  /** 컬럼 너비 조절 */
  resizable?: boolean;
  width?: string;
  height?: string;
  /** @deprecated 헤더는 항상 세로 스크롤에서 제외되어 고정됩니다 (body만 세로 스크롤) */
  stickyHeader?: boolean;
  /** 고정할 좌/우 컬럼 수 */
  stickyColumn?: STableStickyColumn;
  /** border-radius 제어 */
  radius?: 'default' | 'useTop' | 'full';
  noDataLabel?: string;
  /**
   * 데이터가 없을 때 body 영역 전체를 대체하는 슬롯.
   * 지정하면 `noDataLabel` 대신 이 콘텐츠가 헤더 아래 영역을 채우며, 버튼 등 인터랙션도 동작한다.
   */
  noDataSlot?: ReactNode;
  isLoading?: boolean;
  dense?: boolean;
  /** true면 행에 마우스를 올려도 hover 배경(grey_05)을 표시하지 않는다 */
  noHover?: boolean;
  /** 페이지네이션 (있으면 하단 표시) */
  pagination?: STablePagination;
  onPageChange?: (page: number) => void;
  /** 테이블 내부에서 페이지네이션을 직접 관리 (rows를 내부 슬라이싱) */
  useInternalPagination?: boolean;
  /** 페이지당 행 수 셀렉트 표시 */
  useRowsPerPageSelect?: boolean;
  rowsPerPageOption?: SSelectOption[];
  onRowsPerPageChange?: (perPage: number) => void;
  /** 가상 스크롤 */
  useVirtualScroll?: boolean;
  rowHeight?: number;
  virtualBuffer?: number;
  virtualEndThreshold?: number;
  onVirtualUpdate?: (range: { from: number; to: number }) => void;
  onVirtualReachEnd?: (info: {
    from: number;
    to: number;
    rowCount: number;
    threshold: number;
  }) => void;
  /** 테이블 식별자 (없으면 자동 생성) */
  tableId?: string;
  /** 행 클릭 */
  onRowClick?: (row: SRow) => void;
  className?: string;
  style?: CSSProperties;
}

const cellValue = (col: STableColumn, row: SRow) => {
  const raw = typeof col.field === 'function' ? col.field(row) : row[col.field];
  return col.format ? col.format(raw, row) : raw;
};

const colWidthFromDef = (c: STableColumn) => (c.autoWidth ? 0 : parseInt(c.width || '120', 10));

/**
 * STable — sd-table 포팅. 컬럼/행 + 선택 + sticky 헤더/컬럼 + 리사이즈 +
 * 내부 페이지네이션 + rows-per-page + 가상 스크롤 + 로딩 + dense.
 */
export const STable = forwardRef<STableHandle, STableProps>(function STable(
  {
    columns = [],
    rows = [],
    rowKey = 'id',
    selectable = false,
    selected = [],
    onSelectedChange,
    resizable = false,
    width,
    height,
    stickyColumn,
    radius = 'default',
    noDataLabel = '데이터가 없습니다.',
    noDataSlot,
    isLoading = false,
    dense = false,
    noHover = false,
    pagination,
    onPageChange,
    useInternalPagination = false,
    useRowsPerPageSelect = false,
    rowsPerPageOption = DEFAULT_ROWS_PER_PAGE_OPTION,
    onRowsPerPageChange,
    useVirtualScroll = false,
    rowHeight,
    virtualBuffer = 5,
    virtualEndThreshold = 10,
    onVirtualUpdate,
    onVirtualReachEnd,
    tableId,
    onRowClick,
    className,
    style,
  }: STableProps,
  ref,
) {
  const autoId = useId();
  const resolvedTableId = tableId ?? autoId;

  const visibleCols = useMemo(() => columns.filter(c => c.visible !== false), [columns]);
  const stickyLeft = stickyColumn?.left ?? 0;
  const stickyRight = stickyColumn?.right ?? 0;
  const effectiveRowHeight = rowHeight ?? (dense ? DENSE_ROW_HEIGHT : DEFAULT_ROW_HEIGHT);

  // ── 컬럼 너비 (리사이즈로 변경 가능) ──
  const colSignature = useMemo(
    () => visibleCols.map(c => `${c.name}:${c.width ?? ''}:${c.autoWidth ? 1 : 0}`).join('|'),
    [visibleCols],
  );
  const [columnWidths, setColumnWidths] = useState<number[]>(() =>
    visibleCols.map(colWidthFromDef),
  );
  // resize 시작폭을 동기적으로 읽기 위한 ref (setState 업데이터 읽기는 비동기라 신뢰 불가)
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;
  useEffect(() => {
    setColumnWidths(visibleCols.map(colWidthFromDef));
    // colSignature가 실제 재시드 트리거 (visibleCols 참조 변화 무시)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colSignature]);

  // ── 선택 상태 (controlled) ──
  const selectedKeys = useMemo(() => new Set(selected.map(r => r[rowKey])), [selected, rowKey]);
  const isRowSelected = useCallback(
    (row: SRow) => selectedKeys.has(row[rowKey]),
    [selectedKeys, rowKey],
  );
  const getIsAllChecked = useCallback(
    (rws: SRow[]): boolean | null => {
      const total = rws.length;
      if (total === 0) return false;
      const cnt = rws.filter(r => selectedKeys.has(r[rowKey])).length;
      if (cnt === 0) return false;
      if (cnt === total) return true;
      return null;
    },
    [selectedKeys, rowKey],
  );

  // ── 페이지네이션 상태 ──
  const [currentPage, setCurrentPage] = useState(pagination?.page ?? 1);
  const [innerRowsPerPage, setInnerRowsPerPage] = useState(pagination?.rowsPerPage ?? 10);
  useEffect(() => {
    if (pagination?.page != null) setCurrentPage(pagination.page);
    if (pagination?.rowsPerPage != null) setInnerRowsPerPage(pagination.rowsPerPage);
  }, [pagination?.page, pagination?.rowsPerPage]);

  // ── 가상 스크롤 상태 ──
  const [rowCountOverride, setRowCountOverride] = useState<number | null>(null);
  const rowCount = rowCountOverride ?? rows.length;
  const scrollRef = useRef<HTMLDivElement>(null);
  // 헤더는 세로 스크롤 컨테이너 밖에 두고, 가로 스크롤만 body와 동기화한다
  const headerScrollRef = useRef<HTMLDivElement>(null);
  // body 세로 스크롤바 폭. 헤더 우측에 같은 폭의 거터를 둬 두 스크롤포트 너비를 맞춘다
  const [headerGutter, setHeaderGutter] = useState(0);
  const [vsRange, setVsRange] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const lastReachEndRef = useRef<number>(-1);
  const cbRef = useRef({ onVirtualUpdate, onVirtualReachEnd });
  cbRef.current = { onVirtualUpdate, onVirtualReachEnd };

  const [scrolledLeft, setScrolledLeft] = useState(false);
  const [scrolledRight, setScrolledRight] = useState(false);

  const setRowCount = useCallback((count: number) => {
    const safe = Math.max(0, Math.floor(Number.isFinite(count) ? count : 0));
    lastReachEndRef.current = -1;
    setRowCountOverride(safe);
  }, []);

  const calcRange = useCallback(
    (scrollTop: number, containerHeight: number) => {
      const h = Math.max(1, effectiveRowHeight);
      const buf = Math.max(0, Math.floor(virtualBuffer));
      const first = Math.floor(Math.max(0, scrollTop) / h);
      const vis = Math.ceil(Math.max(0, containerHeight) / h);
      return { start: Math.max(0, first - buf), end: Math.min(rowCount, first + vis + buf) };
    },
    [effectiveRowHeight, virtualBuffer, rowCount],
  );

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    // body가 슬롯으로 대체돼 스크롤포트가 없으면 헤더 거터도 없앤다
    if (!el) {
      setHeaderGutter(0);
      return;
    }
    const header = headerScrollRef.current;
    if (header) header.scrollLeft = el.scrollLeft;
    setHeaderGutter(el.offsetWidth - el.clientWidth);
    setScrolledLeft(el.scrollLeft > 0);
    setScrolledRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    if (useVirtualScroll) {
      const { start, end } = calcRange(el.scrollTop, el.clientHeight);
      setVsRange(prev => (prev.start === start && prev.end === end ? prev : { start, end }));
    }
  }, [useVirtualScroll, calcRange]);

  // 초기/의존성 변경 시 범위·스크롤 그림자·헤더 거터 재계산
  // (isLoading/height는 body 스크롤바 유무를 바꿔 headerGutter에 영향)
  useEffect(() => {
    handleScroll();
  }, [handleScroll, rowCount, columnWidths, isLoading, height]);

  // 가상 스크롤 이벤트 발생
  useEffect(() => {
    if (!useVirtualScroll) return;
    cbRef.current.onVirtualUpdate?.({ from: vsRange.start, to: vsRange.end });
    const threshold = Math.max(1, virtualEndThreshold);
    if (vsRange.end >= Math.max(0, rowCount - threshold) && lastReachEndRef.current !== rowCount) {
      lastReachEndRef.current = rowCount;
      cbRef.current.onVirtualReachEnd?.({
        from: vsRange.start,
        to: vsRange.end,
        rowCount,
        threshold,
      });
    }
  }, [vsRange, useVirtualScroll, rowCount, virtualEndThreshold]);

  // ── sticky 스타일 계산 (sd-table getStickyStyleSync 포팅) ──
  const getStickyStyle = useCallback(
    (colIdx: number): CSSProperties => {
      const leftOffset = columnWidths
        .slice(0, colIdx)
        .reduce((a, b) => a + b, selectable ? SELECTABLE_COLUMN_WIDTH : 0);
      const rightOffset = columnWidths
        .filter((_, i) => i >= visibleCols.length - stickyRight && i > colIdx)
        .reduce((a, b) => a + b, 0);
      const col = visibleCols[colIdx];
      const s: CSSProperties = {};
      (s as Record<string, string | number>)['--sticky-left-offset'] = `${leftOffset}px`;
      (s as Record<string, string | number>)['--sticky-right-offset'] = `${rightOffset}px`;
      if (!col?.autoWidth) {
        const w = columnWidths[colIdx];
        s.width = w;
        s.minWidth = w;
        s.maxWidth = w;
      }
      return s;
    },
    [columnWidths, selectable, visibleCols, stickyRight],
  );

  useImperativeHandle(
    ref,
    (): STableHandle => ({
      isRowSelected,
      getIsAllChecked,
      getPaginationInfo: () => {
        if (!useInternalPagination || !pagination) return null;
        const startIndex = (currentPage - 1) * innerRowsPerPage;
        return {
          startIndex,
          endIndex: startIndex + innerRowsPerPage,
          currentPage,
          rowsPerPage: innerRowsPerPage,
        };
      },
      getStickyStyle,
      setRowCount,
      updateRowSelect: row => {
        if (selectedKeys.has(row[rowKey]))
          onSelectedChange?.(selected.filter(r => r[rowKey] !== row[rowKey]));
        else onSelectedChange?.([...selected, row]);
      },
      toggleSelectAll: (checked, rws) => {
        if (checked) {
          const add = rws.filter(r => !selectedKeys.has(r[rowKey]));
          onSelectedChange?.([...selected, ...add]);
        } else {
          const keys = new Set(rws.map(r => r[rowKey]));
          onSelectedChange?.(selected.filter(r => !keys.has(r[rowKey])));
        }
      },
    }),
    [
      isRowSelected,
      getIsAllChecked,
      getStickyStyle,
      setRowCount,
      useInternalPagination,
      pagination,
      currentPage,
      innerRowsPerPage,
      selected,
      selectedKeys,
      onSelectedChange,
      rowKey,
    ],
  );

  // ── 컬럼 리사이즈 (sd-table handleResize 포팅) ──
  const startResize = useCallback(
    (index: number, event: ReactMouseEvent) => {
      if (typeof document === 'undefined') return;
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = columnWidthsRef.current[index] ?? 0;
      const col = visibleCols[index];
      const minWidth = col?.minWidth || 50;
      const maxWidth = col?.maxWidth || 9999;

      const onMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.min(Math.max(startWidth + delta, minWidth), maxWidth);
        setColumnWidths(prev => prev.map((w, i) => (i === index ? newWidth : w)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [visibleCols],
  );

  // ── 행 윈도잉 (가상 스크롤 / 내부 페이지네이션) ──
  const pageInfo =
    useInternalPagination && pagination
      ? {
          startIndex: (currentPage - 1) * innerRowsPerPage,
          endIndex: (currentPage - 1) * innerRowsPerPage + innerRowsPerPage,
        }
      : null;

  let bodyRows = rows;
  let topSpacer = 0;
  let bottomSpacer = 0;
  if (useVirtualScroll) {
    bodyRows = rows.slice(vsRange.start, vsRange.end);
    topSpacer = vsRange.start * effectiveRowHeight;
    bottomSpacer = Math.max(0, (rowCount - vsRange.end) * effectiveRowHeight);
  } else if (pageInfo) {
    bodyRows = rows.slice(pageInfo.startIndex, pageInfo.endIndex);
  }

  // 데이터 없음 상태 (로딩 중이면 로딩이 우선)
  const isNoData = rowCount === 0 && !isLoading;
  // 슬롯이 주어지면 기본 라벨 오버레이 대신 body 영역 자체를 슬롯으로 대체한다.
  const showNoDataSlot = isNoData && noDataSlot != null;

  // ── 페이지네이션 표시 (가상 스크롤과 상호 배타) ──
  const showPagination = !useVirtualScroll && (pagination != null || useInternalPagination);
  const lastPage = useInternalPagination
    ? Math.max(1, Math.ceil(rowCount / (innerRowsPerPage || 1)))
    : (pagination?.lastPage ?? Math.max(1, Math.ceil(rowCount / (pagination?.rowsPerPage ?? 10))));
  const displayPage = useInternalPagination ? currentPage : (pagination?.page ?? 1);

  const changePage = (p: number) => {
    if (useInternalPagination) setCurrentPage(p);
    onPageChange?.(p);
  };
  const changeRowsPerPage = (perPage: number) => {
    if (useInternalPagination) {
      setInnerRowsPerPage(perPage);
      const newLast = Math.max(1, Math.ceil(rowCount / (perPage || 1)));
      setCurrentPage(cp => (cp > newLast ? newLast : cp));
    }
    onRowsPerPageChange?.(perPage);
  };

  // ── 선택 토글 (현재 표시 행 기준) ──
  const headerCheck = getIsAllChecked(bodyRows);
  const toggleAll = () => {
    if (headerCheck === true) {
      const pageKeys = new Set(bodyRows.map(r => r[rowKey]));
      onSelectedChange?.(selected.filter(r => !pageKeys.has(r[rowKey])));
    } else {
      const add = bodyRows.filter(r => !selectedKeys.has(r[rowKey]));
      onSelectedChange?.([...selected, ...add]);
    }
  };
  const toggleRow = (row: SRow) => {
    if (selectedKeys.has(row[rowKey]))
      onSelectedChange?.(selected.filter(r => r[rowKey] !== row[rowKey]));
    else onSelectedChange?.([...selected, row]);
  };

  const radiusClass =
    radius === 'full'
      ? 'rounded-none'
      : radius === 'useTop'
        ? 'rounded-b-[var(--cmp-table-radius)] rounded-t-none'
        : 'rounded-[var(--cmp-table-radius)]';
  const cellPadY = dense
    ? 'var(--cmp-table-body-paddingY-dense)'
    : 'var(--cmp-table-body-paddingY-default)';
  // dense 여부와 무관하게 좌우 패딩은 항상 16px 유지
  const cellPadX = 'var(--cmp-table-body-paddingX-wide)';
  const rowH = dense
    ? 'var(--cmp-table-body-dense-height)'
    : 'var(--cmp-table-body-default-height)';
  const totalColSpan = visibleCols.length + (selectable ? 1 : 0);

  // 특정 시각 컬럼 인덱스의 sticky 배치 정보
  const stickyInfo = (i: number) => {
    const isLeft = i < stickyLeft;
    const isRight = i >= visibleCols.length - stickyRight;
    return { isLeft, isRight, isSticky: isLeft || isRight };
  };
  // 모든 컬럼에 columnWidths 기반 고정폭을 적용(리사이즈 반영)하고, sticky 컬럼은 위치까지 부여.
  // 기존에는 sticky 컬럼에만 폭을 줬기 때문에 resize state가 화면에 반영되지 않았다.
  const cellStyle = (i: number): CSSProperties => {
    const { isLeft, isRight, isSticky } = stickyInfo(i);
    const base = getStickyStyle(i); // 비 autoWidth면 width/min/max + --sticky-*-offset 포함
    if (!isSticky) return base;
    return {
      ...base,
      position: 'sticky',
      left: isLeft ? 'var(--sticky-left-offset)' : undefined,
      right: isRight ? 'var(--sticky-right-offset)' : undefined,
      zIndex: 6,
    };
  };
  // 원본 sd-table의 sticky-left/right-edge :after처럼 그림자를 셀 "바깥"(스크롤 영역) 쪽으로 드리운다.
  // spread 0 → 셀 높이만큼 꽉 찬 코어라 행마다 얼룩 없이 세로로 균일. 색/농도는 원본과 동일하게
  // grey_95(#222)·연한 강도(peak≈20)로 맞춤. 좌측 고정 컬럼 → 오른쪽, 우측 고정 컬럼 → 왼쪽으로 그림자.
  const stickyShadowClass = (i: number) => {
    const { isLeft, isRight } = stickyInfo(i);
    const isLeftEdge = isLeft && i === stickyLeft - 1;
    const isRightEdge = isRight && i === visibleCols.length - stickyRight;
    return cn(
      isLeftEdge && scrolledLeft && 'shadow-[5px_0_8px_0_rgba(34,34,34,0.1)]',
      isRightEdge && scrolledRight && 'shadow-[-5px_0_8px_0_rgba(34,34,34,0.1)]',
    );
  };

  // 헤더 하단 구분선은 body 상단 border로 그린다. body 래퍼는 스크롤바를 포함한
  // 전체 폭을 차지하므로(스크롤바는 border 안쪽) 헤더 거터의 border-right와 미터가
  // 생기지 않아 색·길이가 균일한 한 줄이 된다.
  const bodyScrollClass = cn(
    'min-h-0 flex-1 overflow-auto border-t border-solid border-[color:var(--cmp-table-border-color)]',
    SCROLLBAR_CLASS,
    SCROLLBAR_TRACK_BG_CLASS,
    SCROLLBAR_TRACK_BORDER_CLASS,
  );

  // 헤더/바디 테이블이 분리돼 있어 colgroup으로 두 테이블의 컬럼 폭을 동일하게 고정한다.
  // table-fixed는 첫 행 셀로 폭을 정하는데, 바디 첫 행이 가상 스크롤 스페이서(colSpan)일 수 있어 필수.
  const colgroup = (
    <colgroup>
      {selectable && <col style={{ width: SELECTABLE_COLUMN_WIDTH }} />}
      {visibleCols.map((col, i) => (
        <col key={col.name} style={col.autoWidth ? undefined : { width: columnWidths[i] }} />
      ))}
    </colgroup>
  );

  return (
    <div
      className={cn('flex flex-col', className)}
      style={{ width, ...style }}
      data-table-id={resolvedTableId}
    >
      <div
        className={cn(
          // isolate: no-data/loading 오버레이(z-30·31)를 테이블 안에서 끝낸다. relative + z-auto
          // 만으로는 스태킹 컨텍스트가 생기지 않아, 모달 안에 들어가면 30·31이 모달 Content
          // 레벨까지 올라가 같은 컨텍스트의 floating portal 밴드(z-2)를 덮는다.
          // 자손은 이미 overflow-hidden으로 클리핑되므로 테이블 내부 순서는 달라지지 않는다.
          'bg-white relative isolate flex flex-col overflow-hidden border border-solid border-[color:var(--cmp-table-border-color)]',
          // height가 없으면 루트가 받은 높이를 끝까지 채운다. 호스트가 className으로
          // `flex-1 min-h-0` 등을 줘 루트 높이가 확정된 경우, 행이 적어도 테두리 박스가
          // 남은 영역을 그대로 차지한다. basis는 auto로 둬 높이가 확정되지 않은
          // 기본 사용(내용만큼 커지는 케이스)에서는 동작이 바뀌지 않는다.
          !height && 'min-h-0 grow',
          radiusClass,
          // 원본 sd-table__scroll-container--loading/--no-data: 로딩·데이터 없음 시 인터랙션 차단
          (isLoading || rowCount === 0) && 'pointer-events-none',
        )}
        // 원본: no-data 시 헤더(36px) + 본문 최소(60px) 높이 확보
        style={{
          height,
          minHeight: isNoData ? 'calc(var(--cmp-table-header-height) + 60px)' : undefined,
        }}
      >
        {/* 헤더: 세로 스크롤 밖에 고정, 가로 스크롤은 body와 scrollLeft 동기화.
            border-right 거터로 body 세로 스크롤바 폭만큼 스크롤포트를 맞춰
            sticky right 컬럼·최대 스크롤 위치가 body와 어긋나지 않게 한다. */}
        <div
          ref={headerScrollRef}
          className="flex-none overflow-hidden"
          style={{
            borderRight: headerGutter
              ? `${headerGutter}px solid var(--cmp-table-header-bg)`
              : undefined,
          }}
        >
          <table
            className="w-full table-fixed border-separate border-spacing-0 text-left"
            style={{ fontSize: 12 }}
          >
            {colgroup}
            {/* 원본 .sd-table--no-data sd-thead: 데이터 없음 시 헤더 흐리게 */}
            <thead className={cn(isNoData && 'opacity-40')}>
              <tr className="h-[var(--cmp-table-header-height)] bg-[var(--cmp-table-header-bg)]">
                {selectable && (
                  <th
                    className={cn(
                      // 원본 .th--selected: 48px 고정폭, 항상 left sticky, 체크박스 중앙정렬
                      // 하단 border는 헤더 래퍼가 연속으로 그리므로 셀에는 두지 않는다.
                      'sticky left-0 z-[7] bg-[var(--cmp-table-header-bg)] text-center align-middle',
                      stickyLeft === 0 && scrolledLeft && 'shadow-[5px_0_8px_0_rgba(34,34,34,0.1)]',
                    )}
                    style={{
                      width: SELECTABLE_COLUMN_WIDTH,
                      minWidth: SELECTABLE_COLUMN_WIDTH,
                      maxWidth: SELECTABLE_COLUMN_WIDTH,
                    }}
                  >
                    <div className="flex items-center justify-center">
                      <SCheckbox value={headerCheck} onValueChange={toggleAll} />
                    </div>
                    {/* 원본 th__bar: 체크박스 컬럼 우측 경계 1px 구분선 */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute right-0 top-1/2 h-[16px] w-px -translate-y-1/2 bg-[var(--cmp-table-header-resizingBar-color)]"
                    />
                  </th>
                )}
                {visibleCols.map((col, i) => (
                  <th
                    key={col.name}
                    className={cn(
                      // 하단 border는 헤더 래퍼가 연속으로 그린다 (거터 영역까지 이어짐).
                      'relative bg-[var(--cmp-table-header-bg)] px-[16px] text-[12px] font-medium leading-[20px] text-[color:var(--sys-color-fg-primary)]',
                      stickyInfo(i).isSticky && 'z-[6]',
                      stickyShadowClass(i),
                      col.thClass,
                    )}
                    style={{ textAlign: col.align ?? 'left', ...cellStyle(i), ...col.thStyle }}
                  >
                    <span
                      className={cn(
                        // 원본 th__content: align에 따라 justify-content 적용 (th도 정렬 반영)
                        'inline-flex w-full min-w-0 items-center gap-[4px]',
                        col.align === 'center' && 'justify-center',
                        col.align === 'right' && 'justify-end',
                      )}
                    >
                      {col.renderHeader ? (
                        col.renderHeader(col, i)
                      ) : (
                        <>
                          {col.icon && <SIcon name={col.icon} size={16} color={col.iconColor} />}
                          <span className="truncate">{col.label}</span>
                        </>
                      )}
                    </span>
                    {resizable && !col.autoWidth && i < visibleCols.length - 1 && (
                      // 원본 th__resizer: 셀 우측 경계 안쪽 4px 그립(좌우 1px #CCCCCC 라인, 세로 16px).
                      // 셀 밖으로 넘치면 다음 셀에 가려져 클릭이 안 먹으므로 translate 없이 셀 내부에 배치.
                      // 겉보기 그립은 4px(원본과 동일), 클릭 영역은 9px로 넓혀 사용성 확보.
                      <span
                        onMouseDown={e => startResize(i, e)}
                        className="absolute right-0 top-1/2 z-[5] flex h-[16px] w-[9px] -translate-y-1/2 cursor-col-resize items-center justify-end"
                        aria-hidden
                      >
                        <span className="h-full w-[4px] border-x border-solid border-[color:var(--cmp-table-header-resizingBar-color)]" />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        {/* 데이터 없음 + 슬롯: body 테이블 대신 슬롯이 헤더 아래 영역을 그대로 차지한다.
            상위 박스가 no-data일 때 pointer-events-none이므로 슬롯에서 다시 활성화해
            버튼 등 인터랙티브 콘텐츠가 동작하게 한다. */}
        {showNoDataSlot ? (
          <div className={cn(bodyScrollClass, 'pointer-events-auto bg-white')}>
            <div className="flex h-full min-h-[60px] w-full items-center justify-center">
              {noDataSlot}
            </div>
          </div>
        ) : (
          /* 바디: 세로 스크롤은 이 컨테이너에만 생긴다 (헤더 제외) */
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={cn(
              bodyScrollClass,
              // 원본 sd-table__scroll-container--loading/--no-data: 로딩·데이터 없음 시 스크롤 차단
              (isLoading || rowCount === 0) && 'overflow-hidden',
            )}
          >
            {/* border-collapse:collapse는 sticky 셀의 border가 스크롤 시 사라져 원본처럼 separate 사용 */}
            <table
              className="w-full table-fixed border-separate border-spacing-0 text-left"
              style={{ fontSize: 12 }}
            >
              {colgroup}
              <tbody>
                {useVirtualScroll && topSpacer > 0 && (
                  <tr aria-hidden style={{ height: topSpacer }}>
                    <td colSpan={totalColSpan} className="p-0" />
                  </tr>
                )}
                {rowCount === 0
                  ? null
                  : bodyRows.map((row, ri) => {
                      const isSel = selectedKeys.has(row[rowKey]);
                      // 원본 sd-table: 선택 행 배경 강조 없음. sticky 셀은 불투명 흰색만 유지.
                      const rowBg = 'bg-white';
                      return (
                        <tr
                          key={row[rowKey] ?? ri}
                          onClick={() => onRowClick?.(row)}
                          // border-separate에서는 tr border가 렌더되지 않아 행 구분선은 각 셀(td)의 border-b로 그린다.
                          // 원본 sd-tr: hover 시 grey_05(#F9F9F9)
                          className={cn(
                            'group/table-row',
                            !noHover && 'hover:bg-[var(--color-grey-05)]',
                            onRowClick && 'cursor-pointer',
                          )}
                          style={{ height: useVirtualScroll ? effectiveRowHeight : rowH }}
                        >
                          {selectable && (
                            <td
                              className={cn(
                                // 원본 .td--selected: 48px 고정폭, 항상 left sticky, 체크박스 중앙정렬
                                'sticky left-0 z-[2] border-b border-solid border-[color:var(--cmp-table-border-color)] align-middle',
                                rowBg,
                                !noHover && 'group-hover/table-row:bg-[var(--color-grey-05)]',
                                stickyLeft === 0 &&
                                  scrolledLeft &&
                                  'shadow-[5px_0_8px_0_rgba(34,34,34,0.1)]',
                              )}
                              style={{
                                width: SELECTABLE_COLUMN_WIDTH,
                                minWidth: SELECTABLE_COLUMN_WIDTH,
                                maxWidth: SELECTABLE_COLUMN_WIDTH,
                              }}
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center">
                                <SCheckbox value={isSel} onValueChange={() => toggleRow(row)} />
                              </div>
                            </td>
                          )}
                          {visibleCols.map((col, i) => {
                            const tdClassName = cn(
                              // 원본 .td: 컬럼 폭에 맞춰 내용 말줄임(overflow hidden + ellipsis) + 행 구분선(border-b)
                              // 셀 배경은 흰색으로 채워 뒤 배경이 비치지 않게 하고, hover 시 행 배경(grey_05)을 따른다.
                              'overflow-hidden text-ellipsis whitespace-nowrap border-b border-solid border-[color:var(--cmp-table-border-color)] text-[12px] leading-[20px] text-[color:var(--sys-color-fg-primary)]',
                              rowBg,
                              !noHover && 'group-hover/table-row:bg-[var(--color-grey-05)]',
                              stickyShadowClass(i),
                              typeof col.tdClass === 'function' ? col.tdClass(row) : col.tdClass,
                            );
                            const tdStyle: CSSProperties = {
                              paddingLeft: cellPadX,
                              paddingRight: cellPadX,
                              paddingTop: cellPadY,
                              paddingBottom: cellPadY,
                              textAlign: col.align ?? 'left',
                              ...cellStyle(i),
                            };
                            // td 자체를 호스트가 그리는 경로. colSpan/rowSpan을 직접 줄 수 있고,
                            // 병합에 덮이는 자리는 null을 반환해 td를 아예 만들지 않는다.
                            if (col.renderCell) {
                              const cell = col.renderCell({
                                row,
                                value: cellValue(col, row),
                                column: col,
                                colIndex: i,
                                rowIndex: ri,
                                isSelected: isSel,
                                className: tdClassName,
                                style: tdStyle,
                              });
                              if (cell == null || cell === false) return null;
                              // 반환 엘리먼트에는 key가 없으므로 Fragment로 감싸 key를 부여한다
                              return <Fragment key={col.name}>{cell}</Fragment>;
                            }
                            return (
                              <td key={col.name} className={tdClassName} style={tdStyle}>
                                {col.render
                                  ? col.render(row, cellValue(col, row))
                                  : cellValue(col, row)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                {useVirtualScroll && bottomSpacer > 0 && (
                  <tr aria-hidden style={{ height: bottomSpacer }}>
                    <td colSpan={totalColSpan} className="p-0" />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {isNoData && (
          <>
            {/* 원본 .sd-table__no-data-header-overlay: 헤더 영역 흰색 60% 오버레이 */}
            <div
              aria-hidden
              className="pointer-events-none absolute left-0 right-0 top-0 z-[31] h-[var(--cmp-table-header-height)] bg-white/60"
            />
            {/* 원본 .sd-table__no-data: 헤더 아래 영역에 메시지 정중앙 + 흰색 60% 오버레이.
                슬롯이 있으면 body가 이미 슬롯으로 대체돼 있어 이 오버레이는 그리지 않는다. */}
            {!showNoDataSlot && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 top-[var(--cmp-table-header-height)] z-[30] flex items-center justify-center bg-white/60 text-[12px] text-[color:var(--color-grey-65)]">
                <div className="flex min-h-[60px] w-full items-center justify-center">
                  {noDataLabel}
                </div>
              </div>
            )}
          </>
        )}

        {isLoading && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
            <SCircleProgress indeterminate />
          </div>
        )}
      </div>

      {showPagination && (
        // 원본 sd-table__pagination: 테이블 하단에 이어붙는 회색 바(높이 48px, margin-top:-1px로 테두리 겹침).
        // SPagination은 전체 폭 기준 중앙, rows-per-page SSelect는 우측에 absolute 배치해 중앙 정렬을 유지한다.
        // 루트 높이가 확정된 경우(테이블이 남은 영역을 채우는 배치) 페이지네이션 바가
        // 눌리지 않도록 shrink-0으로 48px를 보장한다.
        <div className="relative -mt-px flex h-[48px] shrink-0 items-center justify-center rounded-[var(--cmp-table-radius)] border border-solid border-[color:var(--cmp-table-border-color)] bg-[var(--color-grey-05)]">
          <SPagination currentPage={displayPage} lastPage={lastPage} onPageChange={changePage} />
          {useRowsPerPageSelect && (
            <div className="absolute right-[10px] top-1/2 -translate-y-1/2">
              <SSelect
                options={rowsPerPageOption}
                value={useInternalPagination ? innerRowsPerPage : pagination?.rowsPerPage}
                emitValue
                onValueChange={v => changeRowsPerPage(Number(v))}
                width={128}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});
